import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable } from '@fortawesome/free-solid-svg-icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import {
  ArrowDownToLineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableIcon,
  GridIcon,
} from '@revfluence/fresh-icons/solid';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { SearchInput } from '@frontend/app/refresh-components';
import { SortingState } from '@tanstack/react-table';
import { ColumnsIconV3 } from '@frontend/app/components';
import { DownloadItem, useImageDownloader, useMessagingContext } from '@frontend/hooks';
import {
  useClientFeatureEnabled,
  useGetTableConfigurationByName,
  useSaveTableConfigurationMutation,
} from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';
import { logger } from '@common';
import { DialogTrigger } from '@frontend/shadcn/components/ui/dialog';
import { useAssignPostsMutation } from '@frontend/app/hooks/useAssignPosts';
import { message } from '@revfluence/fresh';
import { BoostPostFormValues } from '@frontend/applications/Social/components/content-preview/types/boost-post-schema';
import { useLocation } from 'react-router-dom';
import { MetaAdAccount, InstagramAccount } from '@frontend/gql/social/graphql';
import { ViewMode } from '../../types';
import MembersCard from '../members-card';
import ReachCard from '../reach-card';
import EngagementCard from '../engagement-card';
import ImpressionsCard from '../impressions-card';
import PostsCard from '../post-card';
import TMVCard from '../tmv-card';
import PostCardView from '../post-card-view';
import PostsBreakdownByPostTypeHistoricalCard from '../posts-breakdown-by-post-type-historical-card';
import PostsBreakdownByNetworkTotalCard from '../posts-breakdown-by-network-total-card';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
  TableHeaderDropdownButton,
  TableHeaderPagination,
  useTablePagination,
} from '../table-header';
import { usePostsTable } from '../table/hooks/usePostsTable';
import PostTable from '../table/PostTable';
import { POST_TABLE_COLUMN_CONFIG_VERSION, getColumnsWithHiddenColumns } from '../table/config/defaultPostTableColumns';
import ColumnDialog from '../columnSelectorDialog';
import { useExportPostsCsv } from '../../hooks/useExpostPostsCsv';
import AssignPostsDialog from '../assignPostsDialog';
import { useAssignPostsDialog } from '../assignPostsDialog/hooks/useAssignPostsDialog';
import { Group, Project } from '../filters-form/types';
import { EnrichedPost, EnrichedPostsData } from '../../hooks/usePostsData';
import {
  ContentPreviewSheet,
  ContentPreviewBoost,
  useContentPreview,
  postAdapter,
} from '../../../components/content-preview';
import { ContentPreviewData } from '../../../components/content-preview/types';
import { useSimpleColumnDialog } from '../columnSelectorDialog/hooks/useSimpleColumnDialog';
import { useBoostPostServices } from './posts/hooks/useBoostPostServices';

export interface PostsTabProps {
  projects: Project[];
  groups: Group[];
  data?: EnrichedPostsData;
  isLoading: boolean;
  isPartialLoading: boolean;
  selectedColumns: string[];
  setCurrentPage: (page: number) => void;
  currentPage: number;
  setSelectedColumns: (columns: string[]) => void;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
  viewMode: ViewMode;
  setViewMode: (value: ViewMode) => void;
  dates: {
    startDate?: string;
    endDate?: string;
  };
  updateLocalAssignment: (params: {
    entityId: string;
    projectId?: string;
    projectName?: string;
    groupId?: string;
    groupName?: string;
  }) => void;
  metaAdAccounts: MetaAdAccount[] | undefined;
  instagramAccounts?: InstagramAccount[];
  postDetails?: EnrichedPost | null;
}

type ExportFormat = 'current' | 'full';
const TABLE_PAGE_SIZE = 50;

export const PostsTab = ({
  projects,
  groups,
  data,
  isLoading,
  isPartialLoading,
  selectedColumns,
  setSelectedColumns,
  sorting,
  setSorting,
  setCurrentPage,
  currentPage,
  viewMode,
  setViewMode,
  dates,
  updateLocalAssignment,
  metaAdAccounts,
  instagramAccounts,
  postDetails,
}: PostsTabProps) => {
  const location = useLocation();
  const posts = data?.client?.socialAnalytics?.posts?.results || [];
  const pagination = data?.client?.socialAnalytics?.posts?.pagination;
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  const postsBreakdownData = data?.client?.socialAnalytics?.postsBreakdown;

  const [searchText, setSearchText] = useState('');
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [saveTableConfiguration] = useSaveTableConfigurationMutation();
  const [assignPosts] = useAssignPostsMutation();

  const { downloadImages } = useImageDownloader({
    onProgress: (progress: number) => {
      message.info(`Download progress: ${Math.round(progress * 100)}%`);
    },
  });

  const contentPreviewBoost = useClientFeatureEnabled(ClientFeature.CONTENT_PREVIEW_BOOST);
  const ContentPreview = contentPreviewBoost ? ContentPreviewBoost : ContentPreviewSheet;

  const handleProjectSave = useCallback(
    async (
      projectId: string,
      projectName: string,
      entityId: string,
      mentionId: string,
      updateState?: (updates: {
        projectId?: string | undefined;
        projectName?: string | undefined;
        isEditing?: boolean;
      }) => void,
    ) => {
      logger.info('Starting project save operation:', {
        projectId,
        projectName,
        entityId,
      });

      try {
        if (!projectId) {
          logger.error('Project save failed: Missing project ID');
          showErrorMessage('Project ID is required');
          return;
        }

        if (!entityId) {
          logger.error('Project save failed: Missing entity ID');
          showErrorMessage('No post selected');
          return;
        }

        logger.debug('Calling assignPosts mutation:', {
          mentionIds: [mentionId],
          programIds: [parseInt(projectId, 10)],
          programNames: projectName ? [projectName] : undefined,
        });

        const res = await assignPosts({
          variables: {
            mentionIds: [mentionId],
            programIds: [parseInt(projectId, 10)],
            programNames: projectName ? [projectName] : undefined,
          },
        });

        if (res.data?.assignPosts) {
          // Only update local assignment after successful API response
          if (updateLocalAssignment) {
            logger.debug('Updating local assignment:', {
              entityId,
              projectId,
              projectName,
            });
            updateLocalAssignment({
              entityId,
              projectId,
              projectName,
            });
          }

          logger.info('Project save successful:', {
            entityId,
            projectId,
            projectName,
          });
          showSuccessMessage('Project updated successfully');
        } else {
          logger.error('Project save failed: No data returned from mutation', {
            entityId,
            projectId,
            response: res,
          });
          showErrorMessage('Failed to update project');
        }
      } catch (error) {
        logger.error('Project save failed with error:', {
          entityId,
          projectId,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorObject: error,
        });
        showErrorMessage('Failed to update project');
      } finally {
        // Update editing state after failed save
        updateState?.({
          projectId: undefined,
          projectName: undefined,
          isEditing: false,
        });
      }
    },
    [assignPosts, showSuccessMessage, showErrorMessage, updateLocalAssignment],
  );

  const handleGroupSave = useCallback(
    async (
      groupId: string,
      groupName: string,
      entityId: string,
      mentionId: string,
      updateState?: (updates: {
        groupId?: string | undefined;
        groupName?: string | undefined;
        isEditing?: boolean;
      }) => void,
    ) => {
      try {
        if (!groupId) {
          showErrorMessage('Group ID is required');
          return;
        }

        if (!entityId) {
          showErrorMessage('No post selected');
          return;
        }

        const res = await assignPosts({
          variables: {
            mentionIds: [mentionId],
            communityIds: [parseInt(groupId, 10)],
          },
        });

        if (res.data?.assignPosts) {
          // Only update local assignment after successful API response
          if (updateLocalAssignment) {
            updateLocalAssignment({
              entityId,
              groupId,
              groupName,
            });
          }
          showSuccessMessage('Group updated successfully');

          // Update editing state after successful save
        } else {
          showErrorMessage('Failed to update group');
        }
      } catch (error) {
        showErrorMessage('Failed to update group');
        logger.error('Failed to update group:', error);
      } finally {
        // Update editing state after failed save
        updateState?.({
          groupId: undefined,
          groupName: undefined,
          isEditing: false,
        });
      }
    },
    [assignPosts, showSuccessMessage, showErrorMessage, updateLocalAssignment],
  );

  const handleContentDownload = useCallback(
    async (downloadItem: DownloadItem): Promise<void> => {
      try {
        const timestamp = new Date().toISOString();
        const filename = `social_posts_${timestamp}`;
        logger.info(`Downloading images for post ${downloadItem.entityId}`);
        const result = await downloadImages([downloadItem], filename);
        if (result.failed.length > 0) {
          message.warning(`${result.successful.length} images downloaded successfully, ${result.failed.length} failed`);
          logger.error(`Failed to download images for post ${downloadItem.entityId}: ${result.failed.join(', ')}`);
        }
      } catch (error) {
        message.error(`Failed to download images: ${error instanceof Error ? error.message : 'Unknown error'}`);
        logger.error(`Failed to download images: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    [downloadImages],
  );

  const handleContentShare = useCallback((url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const adapter = useCallback(
    (post: EnrichedPost): ContentPreviewData => ({
      ...postAdapter(post, { startDate: dates.startDate, endDate: dates.endDate }),
      id: post.entityId,
      onDownload: () => {
        handleContentDownload({
          entityId: post.entityId,
          postPermalink: post.postPermalink ?? '',
        });
      },
      onShare: () => {
        handleContentShare(post.postPermalink ?? '');
      },
    }),
    [handleContentDownload, handleContentShare, dates.startDate, dates.endDate],
  );

  const availableProjects = useMemo(
    () => projects.map((project) => ({ id: project.id.toString(), name: project.name })),
    [projects],
  );
  const availableGroups = useMemo(
    () => groups.map((group) => ({ id: group.id.toString(), name: group.name })),
    [groups],
  );

  const availableMetaAdAccounts = useMemo(
    () => metaAdAccounts?.filter((account): account is MetaAdAccount => !!account) ?? [],
    [metaAdAccounts],
  );

  // Create local state to track optimistic updates to ad accounts data
  const [optimisticMetaAdAccounts, setOptimisticMetaAdAccounts] = useState<MetaAdAccount[] | undefined>();

  // Use optimistic data if available, otherwise use the original data
  const effectiveMetaAdAccounts = optimisticMetaAdAccounts || availableMetaAdAccounts;

  // Create stable getter function for current effective ad accounts
  const getCurrentAdAccounts = useCallback(() => {
    return optimisticMetaAdAccounts || availableMetaAdAccounts;
  }, [optimisticMetaAdAccounts, availableMetaAdAccounts]);

  // Initialize boost post services with optimistic state management
  const { boostPostService } = useBoostPostServices(setOptimisticMetaAdAccounts, getCurrentAdAccounts);

  // Debug logging for data structure changes
  useEffect(() => {
    logger.info('🔍 DEBUG - Available Meta Ad Accounts updated:', {
      originalCount: availableMetaAdAccounts?.length || 0,
      optimisticCount: optimisticMetaAdAccounts?.length || 0,
      usingOptimistic: !!optimisticMetaAdAccounts,
      effectiveCount: effectiveMetaAdAccounts?.length || 0,
    });

    if (effectiveMetaAdAccounts && effectiveMetaAdAccounts.length > 0) {
      const firstAccount = effectiveMetaAdAccounts[0];
      logger.info('🔍 DEBUG - First account details:', {
        accountId: firstAccount.id,
        campaignCount: firstAccount.metaAdCampaigns?.length || 0,
        campaigns:
          firstAccount.metaAdCampaigns?.map((c) => ({
            id: c?.id,
            name: c?.name,
            adSetCount: c?.metaAdSets?.length || 0,
            adSets: c?.metaAdSets?.map((as) => ({ id: as?.id, name: as?.name })) || [],
          })) || [],
      });
    }
  }, [availableMetaAdAccounts, optimisticMetaAdAccounts, effectiveMetaAdAccounts]);

  // Debug logging specifically for useContentPreview prop changes
  React.useEffect(() => {
    logger.info('🔍 DEBUG - useContentPreview props updated:', {
      hasEffectiveMetaAdAccounts: !!effectiveMetaAdAccounts,
      effectiveMetaAdAccountsLength: effectiveMetaAdAccounts?.length || 0,
      accountIds: effectiveMetaAdAccounts?.map((acc) => acc.id) || [],
      totalCampaigns: effectiveMetaAdAccounts?.reduce((sum, acc) => sum + (acc.metaAdCampaigns?.length || 0), 0) || 0,
      totalAdSets:
        effectiveMetaAdAccounts?.reduce(
          (sum, acc) =>
            sum +
            (acc.metaAdCampaigns?.reduce(
              (campaignSum, campaign) => campaignSum + (campaign?.metaAdSets?.length || 0),
              0,
            ) || 0),
          0,
        ) || 0,
    });
  }, [effectiveMetaAdAccounts]);

  const { previewProps, setSelectedContent: handleContentSelect } = useContentPreview<EnrichedPost>({
    urlParamName: 'postId',
    adapter,
    items: (() => {
      const urlParams = new URLSearchParams(location.search);
      const currentPostId = urlParams.get('postId');
      return postDetails && postDetails.entityId === currentPostId ? [postDetails] : posts;
    })(),
    getItemId: (post: EnrichedPost) => post.entityId,
    projectEditing: {
      onSave: async (projectId: string, projectName: string) => {
        if (previewProps?.content?.projectEditingState?.updateState) {
          await handleProjectSave(
            projectId,
            projectName,
            previewProps.content.id,
            previewProps.content.mentionId,
            previewProps.content.projectEditingState.updateState,
          );
        }
      },
      onCancel: () => {
        showSuccessMessage('Project edit cancelled');
      },
    },
    groupEditing: {
      onSave: async (groupId: string, groupName: string) => {
        if (previewProps?.content?.groupEditingState?.updateState) {
          await handleGroupSave(
            groupId,
            groupName,
            previewProps.content.id,
            previewProps.content.mentionId,
            previewProps.content.groupEditingState.updateState,
          );
        }
      },
      onCancel: () => {
        showSuccessMessage('Group edit cancelled');
      },
    },
    availableProjects,
    availableGroups,
    availableMetaAdAccounts: effectiveMetaAdAccounts as typeof availableMetaAdAccounts,
    availableInstagramAccounts: instagramAccounts as typeof instagramAccounts,
    onBoostPost: async (
      boostPostFormValues: BoostPostFormValues,
      setFormValue?: (name: string, value: unknown) => void,
    ) => {
      // Use the service to handle the entire boost post workflow
      const result = await boostPostService.createBoostFromPost(
        boostPostFormValues,
        { instagramAccounts },
        setFormValue,
      );

      if (!result.success) {
        logger.error('Boost post creation failed:', {
          error: result.error,
          createdResources: result.createdResources,
        });
        // Re-throw error so the ContentPreviewBoost component can handle it
        throw new Error(result.error || 'Boost post creation failed');
      } else {
        logger.info('Boost post creation completed successfully:', {
          createdResources: result.createdResources,
          successMessage: result.successMessage,
        });

        // Return the success message so ContentPreviewBoost can display it inline
        return { successMessage: result.successMessage };
      }
    },
  });

  const updateGlobalFilterText = (text: string) => {
    setSearchText(text);
    setCurrentPage(0);
  };

  const { data: tableConfigData, loading: tableConfigLoading } = useGetTableConfigurationByName({
    variables: { tableName: 'social-data-posts-table' },
  });

  useEffect(() => {
    if (
      !tableConfigLoading &&
      tableConfigData?.getTableConfigurationByName?.columnDefinitions &&
      tableConfigData?.getTableConfigurationByName?.version === POST_TABLE_COLUMN_CONFIG_VERSION
    ) {
      const newColumns = tableConfigData.getTableConfigurationByName.columnDefinitions;
      setSelectedColumns(newColumns.map((col) => col.column));
    }
  }, [tableConfigLoading, tableConfigData, setSelectedColumns]);

  // Build column definitions while recursively removing hidden columns and sub-columns.
  const hydratedVisibleColumns = useMemo(() => {
    type PostCol = ReturnType<typeof getColumnsWithHiddenColumns>[number];
    type VisiblePostCol = Omit<PostCol, 'hidden'>;

    const filterVisible = (columns: PostCol[]): VisiblePostCol[] =>
      columns
        .filter((c) => !c.hidden)
        .map((c) => {
          const { hidden: _hidden, subColumns, ...rest } = c;

          return subColumns?.length ? { ...rest, subColumns: filterVisible(subColumns as PostCol[]) } : { ...rest };
        });

    // Cast is safe because hidden property is not used downstream after filtering
    return filterVisible(getColumnsWithHiddenColumns(selectedColumns)) as unknown as PostCol[];
  }, [selectedColumns]);

  const {
    postTableProps: postTablePropsBase,
    postTableActions,
    filteredCount,
    filteredData,
  } = usePostsTable({
    selectedColumns: hydratedVisibleColumns,
    setSelectedColumns,
    data: posts,
    currentPage,
    pageSize: TABLE_PAGE_SIZE,
    globalFilter: searchText,
    handleSortingChange: setSorting,
    sorting,
    setIsPartialLoading: () => {
      /* no-op */
    },
  });

  const postTableProps = {
    ...postTablePropsBase,
    onRowClick: handleContentSelect,
  };

  const { handleExportCsv, isExporting } = useExportPostsCsv({
    postInsights: posts.filter((post) => (filteredData || []).some((filtered) => filtered.entityId === post.entityId)),
    selectedColumns: hydratedVisibleColumns,
  });

  const isAnyExporting = isExporting;
  const handleExport = async (format: ExportFormat) => {
    try {
      if (format === 'current') {
        handleExportCsv();
        showSuccessMessage('Selected fields exported successfully');
      } else {
        handleExportCsv(true);
        showSuccessMessage('All fields exported successfully');
      }
    } catch (error) {
      showErrorMessage('Failed to export data');
      logger.error('Export failed:', error);
    }
  };
  const onUnAssignPosts = useCallback(
    async (unassignProject: boolean, unassignGroup: boolean) => {
      try {
        // Ensure at least one assignment target is provided
        if (!unassignProject && !unassignGroup) {
          showErrorMessage('Please select either a project or a group to unassign');
          return;
        }

        const res = await assignPosts({
          variables: {
            mentionIds: postTableProps.selectedRows.map((row) => row.mentionId),
            ...(unassignProject && { programIds: [] }),
            ...(unassignGroup && { communityIds: [] }),
          },
        });

        if (res.data?.assignPosts) {
          // Only update local assignments after successful API response
          postTableProps.selectedRows.forEach((row) => {
            if (updateLocalAssignment) {
              updateLocalAssignment({
                entityId: row.entityId,
                ...(unassignProject && { projectId: undefined, projectName: undefined }),
                ...(unassignGroup && { groupId: undefined, groupName: undefined }),
              });
            }
          });
          showSuccessMessage('Posts successfully unassigned');
        } else {
          showErrorMessage('Failed to unassign posts');
        }
      } catch (error) {
        logger.error(error);
        showErrorMessage('Error unassigning posts');
      }
    },
    [assignPosts, showSuccessMessage, showErrorMessage, postTableProps.selectedRows, updateLocalAssignment],
  );
  const onAssignPosts = useCallback(
    async (programId: string, programName: string, communityId: string) => {
      try {
        // Ensure at least one assignment target is provided
        if (!programId && !communityId) {
          showErrorMessage('Please select either a project or a group to assign posts');
          return;
        }

        const res = await assignPosts({
          variables: {
            mentionIds: postTableProps.selectedRows.map((row) => row.mentionId),
            ...(programId && { programIds: [parseInt(programId, 10)] }),
            ...(programName && { programNames: [programName] }),
            ...(communityId && { communityIds: [parseInt(communityId, 10)] }),
          },
        });

        if (res.data?.assignPosts) {
          // Only update local assignments after successful API response
          postTableProps.selectedRows.forEach((row) => {
            if (updateLocalAssignment) {
              updateLocalAssignment({
                entityId: row.entityId,
                ...(programId && { projectId: programId, projectName: programName }),
                ...(communityId && {
                  groupId: communityId,
                  groupName: groups.find((g) => g.id.toString() === communityId)?.name,
                }),
              });
            }
          });
          showSuccessMessage('Posts successfully assigned');
        } else {
          showErrorMessage('Failed to assign posts');
        }
      } catch (error) {
        logger.error(error);
        showErrorMessage('Error assigning posts');
      }
    },
    [assignPosts, showSuccessMessage, showErrorMessage, postTableProps.selectedRows, updateLocalAssignment, groups],
  );

  const onSaveColumnInfo = useCallback(
    async (columns: string[]) => {
      try {
        await saveTableConfiguration({
          variables: {
            data: {
              version: POST_TABLE_COLUMN_CONFIG_VERSION,
              tableName: 'social-data-posts-table',
              columnDefinitions: columns.map((key) => ({ column: key })), // this expects array of objects, simplify if moving to strings
            },
          },
        });
        setSelectedColumns(columns);
        showSuccessMessage('Columns successfully updated');
      } catch (error) {
        logger.error(error);
        showErrorMessage('error saving columns');
      }
    },
    [saveTableConfiguration, showSuccessMessage, showErrorMessage, setSelectedColumns],
  );

  const { columnDialogProps, openDialog } = useSimpleColumnDialog({
    selectedColumns,
    onSave: onSaveColumnInfo,
  });

  const { assignPostsDialogProps } = useAssignPostsDialog({
    projects,
    groups,
    selectedCount: postTableProps.selectedRows.length,
    onAssign: onAssignPosts,
  });

  const { paginationProps, prevButtonProps, nextButtonProps } = useTablePagination({
    currentPage,
    totalCount: filteredCount,
    pageSize: TABLE_PAGE_SIZE,
    onPageChange: setCurrentPage,
  });

  const handleActionMenuClick = (action: string) => {
    switch (action) {
      case 'download-posts':
        postTableActions.onDownloadPosts();
        break;
      case 'unassign-posts-project':
        onUnAssignPosts(true, false);
        break;
      case 'unassign-posts-group':
        onUnAssignPosts(false, true);
        break;
    }
  };

  if (isLoading && !isPartialLoading) {
    return (
      <>
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4" aria-busy>
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading posts card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading members card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading impressions card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading engagement card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading reach card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading TMV card" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 mb-4">
          <div className="lg:col-span-2">
            <Skeleton className="h-[400px] rounded-xl" aria-label="Loading posts by type chart" />
          </div>
          <div className="lg:col-span-1">
            <Skeleton className="h-[400px] rounded-xl" aria-label="Loading posts by network chart" />
          </div>
        </section>

        {viewMode === 'table' ? (
          <Skeleton className="mx-4 mb-4 rounded-xl" aria-label="Loading posts table">
            <div className="p-4">
              <Skeleton className="h-[60px] w-full rounded-lg pb-4" />
              <div className="space-y-2 pt-4">
                {Array.from({ length: 10 }, (_, i) => (
                  <Skeleton key={`skeleton-row-${currentPage}-${i}`} className="h-[48px] w-full rounded-lg" />
                ))}
              </div>
            </div>
          </Skeleton>
        ) : (
          <Card className="mx-4 mb-4 p-4" aria-label="Loading posts cards">
            <Skeleton className="h-[60px] w-full rounded-lg mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 4xl:grid-cols-7 5xl:grid-cols-8 6xl:grid-cols-10 gap-4 p-4">
              {Array.from({ length: 10 }, (_, i) => (
                <Skeleton key={`skeleton-card-${currentPage}-${i}`} className="aspect-[9/16] w-full rounded-xl" />
              ))}
            </div>
          </Card>
        )}
      </>
    );
  }
  return (
    <>
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
        <PostsCard data={totalInsights} />
        <MembersCard data={totalInsights} />
        <ImpressionsCard data={totalInsights} />
        <EngagementCard data={totalInsights} />
        <ReachCard data={totalInsights} />
        <TMVCard data={totalInsights} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 mb-4">
        <div className="lg:col-span-2">
          <PostsBreakdownByPostTypeHistoricalCard
            data={postsBreakdownData}
            fetching={isLoading}
          />
        </div>
        <div className="lg:col-span-1">
          <PostsBreakdownByNetworkTotalCard
            data={postsBreakdownData}
            fetching={isLoading}
          />
        </div>
      </section>

      <Card className="mx-4 mb-4" aria-label="Members table">
        <TableHeader>
          <TableHeaderLayout>
            <TableHeaderSection>
              <TableHeaderLabel>
                <div className="flex items-center gap-4">
                  <span aria-live="polite">{paginationProps?.totalCount || 0} posts</span>
                </div>
              </TableHeaderLabel>
              <AssignPostsDialog {...assignPostsDialogProps}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <TableHeaderDropdownButton
                      data-dd-action-name="social-data-posts-table-actions"
                      disabled={postTableProps.selectedRows.length === 0}
                      aria-label={
                        postTableProps.selectedRows.length === 0 ? 'Actions (disabled - no posts selected)' : 'Actions'
                      }
                    >
                      <div className="flex items-center">Actions</div>
                    </TableHeaderDropdownButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={postTableProps.selectedRows.length === 0}
                      data-dd-action-name="social-data-posts-table-actions-download-posts"
                      aria-label="Table action to download posts"
                      className="cursor-pointer"
                      onClick={() => handleActionMenuClick('download-posts')}
                    >
                      Download Posts
                    </DropdownMenuItem>
                    <DialogTrigger asChild>
                      <DropdownMenuItem
                        disabled={postTableProps.selectedRows.length === 0}
                        data-dd-action-name="assign-posts"
                        aria-label="Assign posts to campaign or group"
                      >
                        Assign
                      </DropdownMenuItem>
                    </DialogTrigger>
                    <DropdownMenuItem
                      disabled={postTableProps.selectedRows.length === 0}
                      data-dd-action-name="unassign-posts-project-action"
                      aria-label="Table action to unassign posts from project"
                      className="cursor-pointer"
                      onClick={() => handleActionMenuClick('unassign-posts-project')}
                    >
                      Unassign from Project
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={postTableProps.selectedRows.length === 0}
                      data-dd-action-name="unassign-posts-group-action"
                      aria-label="Table action to unassign posts from group"
                      className="cursor-pointer"
                      onClick={() => handleActionMenuClick('unassign-posts-group')}
                    >
                      Unassign from Group
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </AssignPostsDialog>
            </TableHeaderSection>

            <TableHeaderSection>
              <SearchInput searchText={searchText} setSearchText={updateGlobalFilterText} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton data-dd-action-name="export-members-data">
                    <div className="flex items-center">
                      <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleExport('current')}
                    disabled={isAnyExporting}
                    data-dd-action-name="export-current-view"
                    aria-label="Export selected member data fields"
                    className="cursor-pointer"
                  >
                    Export selected fields
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExport('full')}
                    disabled={isAnyExporting}
                    data-dd-action-name="export-full-data"
                    aria-label="Export all member data fields"
                  >
                    Export all fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Consolidated Table Settings menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton data-dd-action-name="posts-table-settings" aria-label="Table settings">
                    <FontAwesomeIcon icon={faTable} className="h-4 w-4" aria-hidden="true" />
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {viewMode === 'table' ? (
                    <DropdownMenuItem
                      onClick={() => {
                        // Close the dropdown (default behavior) and then open the column dialog directly.
                        openDialog();
                      }}
                      data-dd-action-name="posts-table-settings-edit-columns"
                      aria-label="Edit Columns"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <ColumnsIconV3 size={16} aria-hidden="true" />
                      Edit Columns
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      disabled
                      aria-disabled="true"
                      data-dd-action-name="posts-table-settings-edit-columns-disabled"
                      className="flex items-center gap-2 cursor-not-allowed opacity-50"
                    >
                      <ColumnsIconV3 size={16} aria-hidden="true" />
                      Edit Columns
                    </DropdownMenuItem>
                  )}

                  {viewMode === 'table' ? (
                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setViewMode('cards');
                        }, 150);
                      }}
                      data-dd-action-name="posts-table-settings-card-view"
                      aria-label="Switch to card view"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <GridIcon className="h-4 w-4" />
                      Card View
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setViewMode('table');
                        }, 150);
                      }}
                      data-dd-action-name="posts-table-settings-table-view"
                      aria-label="Switch to table view"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <TableIcon className="h-4 w-4" />
                      Table View
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <nav aria-label="Table navigation" className="flex items-center">
                <TableHeaderIconButton
                  {...prevButtonProps}
                  data-dd-action-name="posts-table-previous-page"
                  aria-label="Previous page"
                  aria-disabled={currentPage === 0}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                </TableHeaderIconButton>
                <TableHeaderPagination
                  {...paginationProps}
                  aria-label={`Page ${currentPage + 1} of ${Math.ceil(
                    (pagination?.totalCount || 0) / TABLE_PAGE_SIZE,
                  )}`}
                />
                <TableHeaderIconButton
                  {...nextButtonProps}
                  data-dd-action-name="posts-table-next-page"
                  aria-label="Next page"
                  aria-disabled={!pagination?.hasNextPage}
                >
                  <ChevronRightIcon aria-hidden="true" />
                </TableHeaderIconButton>
              </nav>
            </TableHeaderSection>
          </TableHeaderLayout>
        </TableHeader>
        {viewMode === 'table' ? (
          <PostTable {...postTableProps} aria-busy={isLoading} aria-label="Members data table" />
        ) : (
          <PostCardView
            globalFilter={searchText}
            posts={posts}
            currentPage={currentPage}
            pageSize={TABLE_PAGE_SIZE}
            sorting={sorting}
            onPostClick={handleContentSelect}
            selectedRows={postTableProps.selectedRows}
            onSelectionChange={postTableProps.onSelectionChange}
          />
        )}
      </Card>

      {previewProps && <ContentPreview {...previewProps} />}

      {/* ColumnDialog rendered outside the dropdown to keep the modal mounted */}
      {viewMode === 'table' && <ColumnDialog {...columnDialogProps} />}
    </>
  );
};
